import { Resizable } from 're-resizable';
import { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { FileContext } from '../../../context/FileContext';
import { resolveTextDirection } from '../../../utils/common';

const OuterContainer = styled.div`
  border: 1px solid transparent !important;
  border-radius: 4px !important;
  padding: 2px;

  &.active {
    border: 1px dashed rgba(0, 115, 234, 0.4) !important;
    border-radius: 4px !important;
  }

  &.hover-active:hover {
    border: 1px solid rgba(0, 115, 234, 0.4) !important;
    border-radius: 4px !important;
  }
`;

const TextAreaInput = styled.textarea`
  border: 1px solid #c5c7d0;
  border-radius: 4px;
  width: 100%;
  height: 100%;
  cursor: auto;
  font-size: ${({ fontSize }) => `${fontSize}px`};
  color: ${({ color }) => color};
  text-align: ${({ alignment }) => alignment};
  direction: ${({ direction }) =>
    direction === 'rtl' ? 'rtl' : direction === 'ltr' ? 'ltr' : undefined};
  font-weight: ${({ bold }) => (bold ? 800 : 400)};
  font-style: ${({ italic }) => (italic ? 'italic' : 'normal')};
  font-family: 'Open Sans', sans-serif;
  resize: none;

  &:active,
  &:focus-visible {
    outline: none;
  }

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.3);
  }

  &::-webkit-scrollbar-thumb {
    background-color: grey;
    border-radius: 4rem;
  }
`;

const TextAreaEditOverlayChip = ({ item, setIsResizing }) => {
  // height of container for text box on initialization
  const defaultHeight = 18.33;
  const defaultWidth = 150;
  const activeFontSize = item?.fontStyle?.fontSize || 11;
  const activeColor =
    `rgb(${item?.fontStyle?.color.r}, ${item?.fontStyle?.color.g}, ${item?.fontStyle?.color.b})` ||
    'rgb(0,0,0)';
  const activeAlignment = item?.fontStyle?.alignment || 'left';
  const isBold = item?.fontStyle?.bold;
  const isItalic = item?.fontStyle?.italics;
  const textDirection = resolveTextDirection(item?.content);

  const {
    addedFormFields,
    updateFormFields,
    currentSelectedItem,
    draggableFieldsData,
  } = useContext(FileContext);

  useEffect(() => {
    if (!item?.width) item.width = defaultWidth;
    if (!item?.height) item.height = defaultHeight;
  }, []);

  const handleResizeStart = (_e, _dir) => {
    setIsResizing(true);
  };

  const handleResizeEnd = (item) => (_e, _dir, _ref, d) => {
    setIsResizing(false);

    item.width += d.width;
    item.height += d.height;

    addedFormFields[currentSelectedItem.current] = item;
    draggableFieldsData[item.itemId] = {
      height: item.height,
      width: item.width,
    };
    updateFormFields([...addedFormFields]);
  };

  const handleInput = (e) => {
    const selectedItem = addedFormFields.find((x) => x.id === item.id);
    selectedItem.content = e.target.value;
    updateFormFields([...addedFormFields]);
  };

  return (
    <>
      <OuterContainer
        fontSize={activeFontSize}
        className={item.isActive ? 'active' : 'hover-active'}
      >
        <Resizable
          size={{
            height: item?.height || defaultHeight,
            width: item?.width || defaultWidth,
          }}
          lockAspectRatio={false}
          enable={{
            top: false,
            right: item?.isActive,
            bottom: item?.isActive,
            left: false,
            topRight: false,
            bottomRight: item?.isActive,
            bottomLeft: false,
            topLeft: false,
          }}
          className={
            item.isActive
              ? 'resizeable resizable-min-dimensions'
              : 'resizable-min-dimensions'
          }
          onResizeStart={handleResizeStart}
          onResizeStop={handleResizeEnd(item)}
        >
          <TextAreaInput
            height={item.height}
            width={item.width}
            fontSize={activeFontSize}
            color={activeColor}
            alignment={activeAlignment}
            bold={isBold}
            italic={isItalic}
            value={item.content}
            direction={textDirection}
            dir={textDirection}
            onInput={handleInput}
          ></TextAreaInput>
        </Resizable>
      </OuterContainer>
    </>
  );
};

export default TextAreaEditOverlayChip;
